import React from 'react'
import { Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material'

import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material'
import type { DepartmentListItemProps } from 'types/common'
import { Text1, Caption } from 'ui/shorthands/Texts'
import { LabelWithStatus } from 'ui/shorthands/Labels'
import LinkUrlText from 'ui/utils/LinkUrlText'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  radio: {
    padding: theme.spacing(0, 1),
  },
  item: {
    padding: theme.spacing(2, 2),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  description: {
    display: 'block',
    padding: theme.spacing(1, 4, 0),
  },
  single: {
    paddingLeft: theme.spacing(0),
  },
}))

export type SelectDepartmentProps = {
  value: number
  name: string
  onSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  departments: DepartmentListItemProps[]
  fixed?: boolean
}

const SelectDepartment: React.FC<SelectDepartmentProps> = ({
  value,
  name,
  onSelect,
  departments,
  fixed,
}: SelectDepartmentProps) => {
  const { classes, cx } = useStyles()

  const staticComponent = () => {
    let department: DepartmentListItemProps | undefined
    if (departments.length === 1) {
      department = departments[0]
    }
    if (fixed && value) {
      department = departments.find((d) => d.id === value)
    }
    if (!department) {
      return <div />
    }
    return (
      <div className={classes.item}>
        <Text1>{department.name}</Text1>
        <Caption className={cx(classes.description, classes.single)}>
          <LinkUrlText>{department.supplementary_text}</LinkUrlText>
        </Caption>
      </div>
    )
  }

  const selectComponent = (department: DepartmentListItemProps) => (
    <div
      className={classes.item}
      key={[department.id, department.name].join('')}
    >
      <FormControlLabel
        key={[department.id, department.name].join()}
        value={department.id}
        control={<Radio className={classes.radio} color="primary" />}
        label={
          <LabelWithStatus status={'required'}>
            <Text1>{department.name}</Text1>
          </LabelWithStatus>
        }
      />
      <Caption className={classes.description}>
        <LinkUrlText>{department.supplementary_text}</LinkUrlText>
      </Caption>
    </div>
  )

  return (
    <div>
      <FormControl component="fieldset" className={classes.root}>
        <RadioGroup name={name} value={value} onChange={onSelect}>
          {departments.length === 1 || fixed
            ? staticComponent()
            : departments.map(
                (department: DepartmentListItemProps) =>
                  department.is_open && selectComponent(department)
              )}
        </RadioGroup>
      </FormControl>
    </div>
  )
}

SelectDepartment.defaultProps = {
  fixed: false,
}

export default SelectDepartment
